import { publicWidgetStates } from '../widgets/permissions';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import apiClient from '../api/client';
import './LayoutGallery.css';
import { layoutPreviewRects } from '../utils/layoutPreview';

const EMPTY_GALLERY = {
  templates: [],
  unlistedTemplates: [],
  canPublish: false,
  publishedCount: 0,
  publishLimit: 5,
  licenseNotice: '',
  liabilityNotice: '',
};

const LayoutGallery = ({
  isOpen,
  onClose,
  currentLayout,
  currentViewName,
  onUseTemplate,
}) => {
  const [gallery, setGallery] = useState(EMPTY_GALLERY);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [isPublishFormOpen, setIsPublishFormOpen] = useState(false);
  const [visibility, setVisibility] = useState('public');
  const [thumbnailMode, setThumbnailMode] = useState('preview');
  const [sharedTemplate, setSharedTemplate] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [licenseAccepted, setLicenseAccepted] = useState(false);
  const [liabilityAccepted, setLiabilityAccepted] = useState(false);

  const loadGallery = async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
      setHasLoaded(false);
      setError('');
    }
    try {
      const response = await apiClient.getLayoutGallery();
      setGallery({ ...EMPTY_GALLERY, ...(response.data || {}) });
      setHasLoaded(true);
    } catch (requestError) {
      if (!silent) {
        setError(
          requestError.status === 404
            ? 'The Layout Gallery API is not available on the running backend. Restart or redeploy the backend to enable it.'
            : requestError.message || 'Unable to load the layout gallery'
        );
      }
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setName(currentViewName || 'My layout');
    setDescription('');
    setVisibility('public');
    setThumbnailMode('preview');
    setLicenseAccepted(false);
    setLiabilityAccepted(false);
    setIsPublishFormOpen(false);
    setNotice('');
    loadGallery();
  }, [currentViewName, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const refreshTimer = window.setInterval(() => {
      loadGallery({ silent: true });
    }, 10000);
    return () => window.clearInterval(refreshTimer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const token = new URLSearchParams(window.location.search).get('layout');
    if (!token) return;
    let active = true;
    apiClient.getSharedLayoutTemplate(token).then(response => {
      if (active) setSharedTemplate(response.data.template);
    }).catch(error => { if (active) setError(error.message || 'Unable to load shared layout'); });
    return () => { active = false; };
  }, [isOpen]);

  if (!isOpen) return null;

  const getShareUrl = (template) => {
    const url = new URL(window.location.href);
    url.searchParams.set('layout', template.shareToken);
    return url.toString();
  };
  const linkedTemplate = sharedTemplate && !gallery.templates.some(t => t.id === sharedTemplate.id)
    && !gallery.unlistedTemplates.some(t => t.id === sharedTemplate.id) ? sharedTemplate : null;

  const atLimit = gallery.publishedCount >= gallery.publishLimit;
  const canSubmit = Boolean(
    name.trim()
    && licenseAccepted
    && liabilityAccepted
    && currentLayout?.widgetStates
    && !isPublishing
    && !atLimit
  );

  const handlePublish = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setIsPublishing(true);
    setError('');
    setNotice('');
    const publishedName = name.trim();
    try {
      const response = await apiClient.publishLayoutTemplate({
        name: publishedName,
        visibility,
        thumbnailMode,
        description: description.trim(),
        layout: { ...currentLayout, widgetStates: publicWidgetStates(currentLayout.widgetStates) },
        licenseAccepted,
        liabilityAccepted,
      });
      setIsPublishFormOpen(false);
      if (response.data?.template?.shareToken) setShareUrl(getShareUrl(response.data.template));
      await loadGallery();
      setNotice(visibility === 'unlisted'
        ? `“${publishedName}” is published as unlisted. Anyone signed in with its share link can visit it.`
        : `“${publishedName}” is published and now appears in the gallery.`);
    } catch (requestError) {
      setError(requestError.message || 'Unable to publish this layout');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleDelete = async (template) => {
    if (isDeleting || !window.confirm(`Remove published layout “${template.name}”? Its share link will stop working. This cannot be undone.`)) return;
    setIsDeleting(true);
    setError('');
    try {
      await apiClient.deleteLayoutTemplate(template.id);
      if (sharedTemplate?.id === template.id) setSharedTemplate(null);
      if (shareUrl === getShareUrl(template)) setShareUrl('');
      await loadGallery();
    } catch (requestError) {
      setError(requestError.message || 'Unable to remove this template');
    } finally {
      setIsDeleting(false);
    }
  };

  const renderTemplateCard = (template) => (
    <article className="layout-template-card" key={template.id}>
      <div className="layout-template-preview" aria-hidden="true">
        {template.thumbnailMode === 'none'
          ? <div className="layout-template-default-icon">▦</div>
          : layoutPreviewRects(template.layout).map(({ id, style }) => <span key={id} style={style} />)}
      </div>
      <div className="layout-template-body">
        <div className="layout-template-heading">
          <div>
            <h3>{template.name}</h3>
            {template.visibility === 'unlisted' && <span>Unlisted</span>}
            <span>by {template.author?.username || 'Unknown'}</span>
          </div>
          <span className="layout-widget-count">{template.visibleWidgetCount} widgets</span>
        </div>
        {template.description && <p>{template.description}</p>}
        <div className="layout-template-actions">
          <button type="button" onClick={() => onUseTemplate(template)}>Use template</button>
          {template.shareToken && <button type="button" onClick={() => setShareUrl(getShareUrl(template))}>Share link</button>}
          {(template.canDelete || template.isOwner) && (
            <button type="button" className="layout-template-delete" disabled={isDeleting} onClick={() => handleDelete(template)}>Remove</button>
          )}
        </div>
      </div>
    </article>
  );

  return createPortal(
    <div className="layout-gallery-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="layout-gallery"
        role="dialog"
        aria-modal="true"
        aria-labelledby="layout-gallery-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="layout-gallery-header">
          <div>
            <span className="layout-gallery-eyebrow">Community templates</span>
            <h2 id="layout-gallery-title">Layout Gallery</h2>
            <p>Start from a shared widget arrangement. Templates never include spike or dataset data.</p>
          </div>
          <button type="button" className="layout-gallery-close" onClick={onClose} aria-label="Close layout gallery">×</button>
        </header>

        {hasLoaded && (
          <div className="layout-gallery-toolbar">
            <div className="layout-gallery-summary">
              <span>{gallery.templates.length} template{gallery.templates.length === 1 ? '' : 's'}</span>
              <button type="button" onClick={() => loadGallery()} disabled={isLoading}>
                {isLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
            {gallery.canPublish ? (
              <button
                type="button"
                className="layout-gallery-publish-toggle"
                disabled={atLimit}
                onClick={() => setIsPublishFormOpen((value) => !value)}
              >
                {atLimit ? `Limit reached (${gallery.publishLimit})` : 'Publish current layout'}
              </button>
            ) : (
              <div className="layout-gallery-publish-access">
                <button
                  type="button"
                  className="layout-gallery-publish-toggle"
                  disabled
                  title="Ask a SpikeScope administrator to change your account role to Pro"
                >
                  Publish current layout
                </button>
                <span className="layout-gallery-pro-note">
                  Publishing requires a Pro or Admin account. Ask an administrator to upgrade your role.
                </span>
              </div>
            )}
          </div>
        )}

        {isPublishFormOpen && gallery.canPublish && (
          <form className="layout-publish-form" onSubmit={handlePublish}>
            <div className="layout-publish-scroll">
              <div className="layout-publish-fields">
                <label>
                  Template name
                  <input value={name} maxLength={120} onChange={(event) => setName(event.target.value)} />
                </label>
                <label>
                  Description <span>(optional)</span>
                  <textarea value={description} maxLength={500} onChange={(event) => setDescription(event.target.value)} />
                </label>
                <label>Visibility
                  <select value={visibility} onChange={event => setVisibility(event.target.value)}>
                    <option value="public">Public — listed in gallery</option>
                    <option value="unlisted">Unlisted — share link only</option>
                  </select>
                </label>
                <p>Public and unlisted layouts both count toward your publishing limit.</p>
                <label>Thumbnail
                  <select value={thumbnailMode} onChange={event => setThumbnailMode(event.target.value)}>
                    <option value="none">No thumbnail (default icon)</option>
                    <option value="preview">Layout preview</option>
                  </select>
                </label>
              </div>
              <div className="layout-publish-consent">
                <label>
                  <input type="checkbox" checked={licenseAccepted} onChange={(event) => setLicenseAccepted(event.target.checked)} />
                  <span>{gallery.licenseNotice}</span>
                </label>
                <label>
                  <input type="checkbox" checked={liabilityAccepted} onChange={(event) => setLiabilityAccepted(event.target.checked)} />
                  <span>{gallery.liabilityNotice}</span>
                </label>
                <p>Only widget placement, size, visibility, and canvas viewport will be published.</p>
              </div>
            </div>
            <div className="layout-publish-actions">
              <span>Accept both terms above to enable publishing.</span>
              <button type="submit" className="layout-publish-submit" disabled={!canSubmit}>
                {isPublishing ? 'Publishing…' : `Confirm and publish layout (${gallery.publishedCount}/${gallery.publishLimit})`}
              </button>
            </div>
          </form>
        )}

        {notice && <div className="layout-gallery-notice" role="status">✓ {notice}</div>}
        {shareUrl && <label className="layout-gallery-notice">Share link
          <input aria-label="Layout share link" readOnly value={shareUrl} onFocus={event => event.target.select()} />
        </label>}
        {error && <div className="layout-gallery-error" role="alert">{error}</div>}

        <div className="layout-gallery-results">
        {linkedTemplate && <section aria-label="Shared layout">
          <h3 className="layout-gallery-section-title">Shared layout</h3>
          <div className="layout-gallery-grid">{renderTemplateCard(linkedTemplate)}</div>
        </section>}
        <div className="layout-gallery-grid">
          {isLoading ? (
            <div className="layout-gallery-empty">Loading templates…</div>
          ) : error ? null : gallery.templates.length === 0 ? (
            <div className="layout-gallery-empty">No public layouts have been published yet.</div>
          ) : gallery.templates.map(renderTemplateCard)}
        </div>

        {gallery.unlistedTemplates.length > 0 && <section aria-label="Your unlisted layouts">
          <h3 className="layout-gallery-section-title">Your unlisted layouts</h3>
          <div className="layout-gallery-grid">{gallery.unlistedTemplates.map(renderTemplateCard)}</div>
        </section>}

        </div>

        <footer className="layout-gallery-footer">
          Shared layouts are community-provided and covered by the gallery license and liability notices.
        </footer>
      </section>
    </div>,
    document.body
  );
};

export default LayoutGallery;
